import { z } from "zod";
import { partialOf, trimmed, uuid } from "./schemas/common";

/**
 * Guest RSVP links: a private link per household where guests reply without an account, ads or cookies.
 * Every rule lives in the API (RsvpService); these are the shapes and limits both sides share.
 */
export const RSVP = {
  maxMealOptions: 8,
  maxMealOptionChars: 40,
  maxMessageChars: 600,
  maxNoteChars: 500,
  maxDietaryChars: 500,
  maxGuestsPerReply: 20,
  /** A link stops working the day after the wedding. */
  linkExpiryDaysAfterWedding: 1,
  maxLinksPerBatch: 500,
} as const;

export const rsvpSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  mealOptions: z.array(trimmed(RSVP.maxMealOptionChars).min(1)).max(RSVP.maxMealOptions).default([]),
  askDietary: z.boolean().default(true),
  askAddress: z.boolean().default(false),
  allowPlusOneName: z.boolean().default(true),
  message: trimmed(RSVP.maxMessageChars).default(""),
  /** Show timeline events marked "guests" on the reply page. */
  showSchedule: z.boolean().default(false),
});
export type RsvpSettings = z.infer<typeof rsvpSettingsSchema>;
export const DEFAULT_RSVP_SETTINGS: RsvpSettings = rsvpSettingsSchema.parse({});
export const updateRsvpSettingsSchema = partialOf(rsvpSettingsSchema);

export const createRsvpInvitationsSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("households") }),
  z.object({ mode: z.literal("guests"), guestIds: z.array(uuid).min(1).max(RSVP.maxLinksPerBatch) }),
]);
export type CreateRsvpInvitations = z.infer<typeof createRsvpInvitationsSchema>;

export const rsvpResponseSchema = z.object({
  guests: z.array(z.object({
    id: uuid,
    rsvp: z.enum(["accepted", "declined"]),
    mealChoice: trimmed(80).default(""),
    dietaryNotes: trimmed(RSVP.maxDietaryChars).default(""),
    plusOneName: trimmed(80).default(""),
    plusOneAttending: z.boolean().nullable().default(null),
  })).min(1).max(RSVP.maxGuestsPerReply),
  note: trimmed(RSVP.maxNoteChars).default(""),
  /** Honoured only when the couple asks for an address. */
  address: trimmed(240).optional(),
});
export type RsvpResponseInput = z.infer<typeof rsvpResponseSchema>;

export type RsvpLinkStatus = "notOpened" | "opened" | "replied" | "revoked" | "expired";

export interface RsvpInvitationDTO {
  id: string;
  /** Null for viewers who cannot edit guests (and on archived weddings): a link is a bearer credential. */
  token: string | null;
  url: string | null;
  householdId: string | null;
  guestId: string | null;
  label: string;
  guestNames: string[];
  status: RsvpLinkStatus;
  expiresAt: string;
  openedAt: string | null;
  respondedAt: string | null;
  responseCount: number;
  createdAt: string;
}

export interface RsvpOverviewDTO {
  settings: RsvpSettings;
  deadline: string | null;
  closed: boolean;
  invitations: RsvpInvitationDTO[];
  counts: { links: number; opened: number; replied: number; households: number; uncovered: number };
}

/** What the guest page may show about each guest: an allow-list, never the row. */
export interface RsvpPublicGuestDTO {
  id: string; firstName: string; lastName: string; rsvp: string; mealChoice: string; dietaryNotes: string;
  plusOneAllowed: boolean; plusOneName: string; plusOneAttending: boolean | null;
}

export interface RsvpScheduleItemDTO { title: string; time: string; durationMinutes: number; location: string }

export interface RsvpPublicDTO {
  coupleTitle: string;
  weddingDate: string;
  /** The venue's name only — never its address (that is the couple's to share). */
  venueName: string;
  message: string;
  deadline: string | null;
  closed: boolean;
  respondedAt: string | null;
  mealOptions: string[];
  askDietary: boolean;
  askAddress: boolean;
  allowPlusOneName: boolean;
  guests: RsvpPublicGuestDTO[];
  schedule: RsvpScheduleItemDTO[];
}
